import { useCallback, useEffect, useRef, useState } from "react";
import { computeRanges, type HighlightRect, rectsForRanges, wrapIndex } from "@/lib/findInText";

/**
 * Content-scoped find for the memo view. Paints translucent overlay rectangles
 * (not the CSS Highlight API — it doesn't paint reliably in some deployed
 * browsers). Recomputes ranges+rects when the term or content changes; navigation
 * only re-colors + scrolls; a ResizeObserver repositions rects on reflow.
 *
 * @param containerRef        the memo content column (the overlay's positioned ancestor)
 * @param contentVersion      changes when rendered content changes (pass `blocks`)
 * @param initialTerm         a `?q=` deep-link term — opens the bar pre-filled
 * @param initialAnchorPresent true when a `?entry=`/`?block=` flash owns the initial scroll
 * @param enabled             false while presenting (find is normal-view only)
 */
export function useMemoFind({
  containerRef,
  contentVersion,
  initialTerm,
  initialAnchorPresent = false,
  enabled = true,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  contentVersion: unknown;
  initialTerm?: string;
  initialAnchorPresent?: boolean;
  enabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [current, setCurrentState] = useState(0);
  const [count, setCount] = useState(0);
  const [rects, setRects] = useState<HighlightRect[]>([]);
  const rangesRef = useRef<Range[]>([]);
  const currentRef = useRef(0);
  const prevTermRef = useRef<string | null>(null);
  const lastSeededRef = useRef<string | null>(null);
  const suppressScrollRef = useRef(false);

  const setCurrent = useCallback((v: number) => {
    currentRef.current = v;
    setCurrentState(v);
  }, []);

  const scrollTo = useCallback((idx: number) => {
    const el = rangesRef.current[idx]?.startContainer.parentElement;
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Seed from a search deep-link — keyed on the term VALUE (not a once-per-mount
  // flag) so a second search into another memo (MemoPage is REUSED, not remounted,
  // when only $memoId changes) still opens + prefills.
  useEffect(() => {
    if (!enabled) return;
    const seed = initialTerm ?? "";
    if (!seed || lastSeededRef.current === seed) return;
    lastSeededRef.current = seed;
    suppressScrollRef.current = initialAnchorPresent; // let the ?entry= flash scroll
    setTerm(seed);
    setOpen(true);
  }, [initialTerm, initialAnchorPresent, enabled]);

  // Recompute ranges + rects on term / content / open change.
  useEffect(() => {
    if (!open || !enabled) {
      rangesRef.current = [];
      setRects([]);
      setCount(0);
      return;
    }
    const id = window.setTimeout(() => {
      const root = containerRef.current;
      const ranges = root && term ? computeRanges(root, term) : [];
      rangesRef.current = ranges;
      setCount(ranges.length);
      const termChanged = prevTermRef.current !== term;
      prevTermRef.current = term;
      const idx = termChanged
        ? 0
        : ranges.length
          ? Math.min(currentRef.current, ranges.length - 1)
          : 0;
      setCurrent(idx);
      setRects(root ? rectsForRanges(ranges, root) : []);
      if (termChanged && ranges.length && !suppressScrollRef.current) scrollTo(idx);
      if (termChanged) suppressScrollRef.current = false;
    }, 60);
    return () => window.clearTimeout(id);
  }, [open, term, contentVersion, enabled, containerRef, scrollTo, setCurrent]);

  // Reposition rects when the content column reflows (window resize, a wrapping
  // change) — the ranges are unchanged, only their geometry moves.
  useEffect(() => {
    if (!open || !enabled) return;
    const root = containerRef.current;
    if (!root || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setRects(rectsForRanges(rangesRef.current, root)));
    ro.observe(root);
    return () => ro.disconnect();
  }, [open, enabled, containerRef]);

  const step = useCallback(
    (dir: 1 | -1) => {
      const n = rangesRef.current.length;
      if (!n) return;
      const next = wrapIndex(currentRef.current, n, dir);
      setCurrent(next); // re-colors the current rect via the overlay re-render
      scrollTo(next);
    },
    [scrollTo, setCurrent],
  );

  const close = useCallback(() => {
    setOpen(false);
    setTerm("");
    prevTermRef.current = null;
    rangesRef.current = [];
    setRects([]);
    setCount(0);
    setCurrent(0);
  }, [setCurrent]);

  // Ctrl/Cmd+F opens our content-scoped find (never while presenting); Esc closes,
  // unless another editable element (an entry/rename input) owns that Esc.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setOpen(true);
        window.requestAnimationFrame(() =>
          (document.querySelector(".flow-memo-find input") as HTMLElement | null)?.focus(),
        );
        return;
      }
      if (open && e.key === "Escape") {
        const ae = document.activeElement as HTMLElement | null;
        const inEditable =
          !!ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable);
        if (inEditable && !ae?.closest?.(".flow-memo-find")) return; // editor owns Esc
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, enabled, close]);

  return { open, term, setTerm, current, count, rects, step, close };
}
